import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Fade,
  Container,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getApiUrl } from "../config";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}`;
const orbFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-18px)}`;
const scanL = keyframes`0%{transform:translateY(-100%)}100%{transform:translateY(3000%)}`;
const ripple = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(234,88,12,.25)}50%{box-shadow:0 0 0 10px rgba(234,88,12,0)}`;
const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.6}`;

// JAMB-themed palettes (warm amber/orange + complementary accents)
const PALETTES = [
  {
    from: "#b45309",
    to: "#f59e0b",
    shadow: "rgba(180,83,9,.28)",
    light: "#fef3c7",
    text: "#92400e",
  },
  {
    from: "#7c3aed",
    to: "#a78bfa",
    shadow: "rgba(124,58,237,.26)",
    light: "#ede9fe",
    text: "#5b21b6",
  },
  {
    from: "#047857",
    to: "#34d399",
    shadow: "rgba(4,120,87,.26)",
    light: "#d1fae5",
    text: "#065f46",
  },
  {
    from: "#b91c1c",
    to: "#f87171",
    shadow: "rgba(185,28,28,.26)",
    light: "#fee2e2",
    text: "#991b1b",
  },
  {
    from: "#1d4ed8",
    to: "#60a5fa",
    shadow: "rgba(29,78,216,.26)",
    light: "#dbeafe",
    text: "#1e40af",
  },
  {
    from: "#0e7490",
    to: "#22d3ee",
    shadow: "rgba(14,116,144,.26)",
    light: "#cffafe",
    text: "#155e75",
  },
];

const ICONS = ["📐", "🔬", "📚", "🌍", "💻", "⚗️", "📊", "🏛️", "🧬", "🎨"];

// ── styled ──
const Root = styled(Box)({
  minHeight: "100vh",
  height: "100vh",
  background: "#f0f3f9",
  fontFamily: "'Sora',sans-serif",
  display: "flex",
  flexDirection: "column",
});

const TopBar = styled(Box)({
  background: "#0c0a00",
  borderBottom: "1px solid rgba(255,255,255,.06)",
  position: "sticky",
  top: 0,
  zIndex: 100,
  boxShadow: "0 4px 24px rgba(0,0,0,.3)",
});

const Hero = styled(Box)({
  background: "linear-gradient(135deg,#0c0a00 0%,#1a0f00 50%,#0c0a00 100%)",
  position: "relative",
  overflow: "hidden",
  padding: "52px 0 88px",
  flexShrink: 0,
});

const HeroGrid = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(251,191,36,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(251,191,36,.045) 1px,transparent 1px)",
  backgroundSize: "52px 52px",
  pointerEvents: "none",
});

const HeroScan = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  height: 2,
  background:
    "linear-gradient(90deg,transparent,rgba(251,191,36,.14),transparent)",
  animation: `${scanL} 8s linear infinite`,
  pointerEvents: "none",
});

const HeroOrb = styled(Box)(({ l, t, size, color, delay }) => ({
  position: "absolute",
  left: l,
  top: t,
  width: size,
  height: size,
  borderRadius: "50%",
  background: color,
  filter: "blur(88px)",
  opacity: 0.5,
  animation: `${orbFloat} ${8 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
}));

const WaveCut = styled(Box)({
  position: "absolute",
  bottom: -2,
  left: 0,
  right: 0,
  height: 64,
  background: "#f0f3f9",
  clipPath: "ellipse(58% 100% at 50% 100%)",
});

const ExamBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(251,191,36,.08)",
  border: "1px solid rgba(251,191,36,.2)",
  borderRadius: 100,
  padding: "6px 16px",
  backdropFilter: "blur(8px)",
  marginBottom: 20,
});

const JambTag = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "linear-gradient(105deg,rgba(180,83,9,.25),rgba(245,158,11,.15))",
  border: "1px solid rgba(245,158,11,.3)",
  borderRadius: 100,
  padding: "4px 14px",
  marginBottom: 12,
});

const SubCard = styled(Box)(({ pal, selected }) => ({
  borderRadius: 20,
  border: selected ? `2px solid ${pal.from}` : "1.5px solid #e2e8f0",
  background: "#fff",
  boxShadow: selected
    ? `0 0 0 4px ${pal.shadow}`
    : "0 2px 12px -4px rgba(15,23,42,.07)",
  transition: "all .32s cubic-bezier(.22,1,.36,1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-9px)",
    boxShadow: `0 22px 52px -12px ${pal.shadow}`,
    borderColor: "transparent",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg,${pal.from},${pal.to})`,
    opacity: selected ? 1 : 0,
    transition: "opacity .32s ease",
  },
  "&:hover::after": { opacity: 1 },
}));

const BeginBtn = styled(Button)(({ pal }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".82rem",
  padding: "9px 16px",
  background: `linear-gradient(105deg,${pal.from},${pal.to})`,
  backgroundSize: "200% auto",
  color: "#fff",
  boxShadow: `0 6px 18px -6px ${pal.shadow}`,
  transition: "all .28s ease",
  "&:hover": {
    animation: `${shimmer} 1s linear infinite`,
    transform: "translateY(-2px)",
    boxShadow: `0 12px 24px -6px ${pal.shadow}`,
  },
}));

const LogoutBtn = styled(Button)({
  borderRadius: 9,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 600,
  fontSize: ".8rem",
  color: "rgba(255,255,255,.55)",
  border: "1.5px solid rgba(255,255,255,.1)",
  padding: "6px 14px",
  transition: "all .22s ease",
  "&:hover": {
    background: "rgba(239,68,68,.15)",
    borderColor: "rgba(239,68,68,.4)",
    color: "#f87171",
  },
});

const InsRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "11px 0",
  borderBottom: "1px solid #f1f5f9",
  "&:last-child": { borderBottom: "none" },
});

// ── component ──
export default function JambSubjectsScreen() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [examName, setExamName] = useState("JAMB Subjects");
  const [examImage, setExamImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
     const API_URL = getApiUrl();
    const token = JSON.parse(localStorage.getItem("authToken"));
    if (!token) {
      navigate("/student-login");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/jamb-subjects/`, {
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load subjects.");
        setExamName(data.exam_name || "JAMB Subjects");
        setExamImage(data.exam_image || "");
        setSubjects(data.subjects || []);
        if (data.exam_image) {
          // retain original background logic if needed
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/student-login");
    }
  };

  const openModal = (subjectId) => setSelectedSubjectId(subjectId);
  const closeModal = () => setSelectedSubjectId(null);
  const startExam = () => {
    if (selectedSubjectId) navigate(`/jamb-exams?subjectId=${selectedSubjectId}`);
  };

  const instructions = [
    "Ensure you have a stable internet connection before starting.",
    "Do not close the browser or turn off your device during the exam.",
    "The exam will auto-submit when the timer reaches zero.",
    "Manage your time wisely across all questions.",
    "You may use plain sheets for calculations or theory answers.",
  ];

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.6,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all .2s ease",
                  "&:hover": { background: "rgba(255,255,255,.14)" },
                }}
              >
                <ArrowBackIcon
                  sx={{ color: "rgba(255,255,255,.7)", fontSize: 18 }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "9px",
                    background: "linear-gradient(135deg,#b45309,#f59e0b)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SchoolIcon sx={{ color: "#fff", fontSize: 17 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 700,
                    color: "#e2e8f0",
                    fontSize: ".92rem",
                  }}
                >
                  LiteUpper
                </Typography>
              </Box>
            </Box>

            {/* JAMB pill in center */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
                background: "rgba(245,158,11,.1)",
                border: "1px solid rgba(245,158,11,.2)",
                borderRadius: 100,
                px: 2,
                py: 0.6,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#fbbf24",
                  boxShadow: "0 0 6px #fbbf24",
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#fbbf24",
                  fontSize: ".72rem",
                  fontWeight: 700,
                  letterSpacing: ".08em",
                }}
              >
                JAMB PORTAL
              </Typography>
            </Box>

            <LogoutBtn
              onClick={handleLogout}
              startIcon={<LogoutIcon sx={{ fontSize: ".95rem !important" }} />}
            >
              Sign Out
            </LogoutBtn>
          </Box>
        </Container>
      </TopBar>

      {/* HERO */}
      <Hero>
        <HeroGrid />
        <HeroScan />
        <HeroOrb
          l="-120px"
          t="-100px"
          size="420px"
          color="radial-gradient(circle,rgba(180,83,9,.45),transparent)"
          delay={0}
        />
        <HeroOrb
          l="60%"
          t="15%"
          size="280px"
          color="radial-gradient(circle,rgba(245,158,11,.22),transparent)"
          delay={2}
        />
        <HeroOrb
          l="28%"
          t="50%"
          size="190px"
          color="radial-gradient(circle,rgba(124,58,237,.18),transparent)"
          delay={3}
        />
        <WaveCut />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={700}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {examImage ? (
                <Box
                  component="img"
                  src={examImage}
                  alt="exam"
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "22px",
                    objectFit: "cover",
                    border: "3px solid rgba(245,158,11,.35)",
                    boxShadow: "0 16px 40px rgba(0,0,0,.35)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "22px",
                    background: "linear-gradient(135deg,#b45309,#f59e0b)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 16px 40px rgba(180,83,9,.4)",
                  }}
                >
                  <SchoolIcon sx={{ color: "#fff", fontSize: 40 }} />
                </Box>
              )}
              <Box>
                <JambTag>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      color: "#fbbf24",
                      fontSize: ".68rem",
                      fontWeight: 700,
                      letterSpacing: ".1em",
                    }}
                  >
                    JOINT ADMISSIONS & MATRICULATION
                  </Typography>
                </JambTag>
                <ExamBadge>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#4ade80",
                      boxShadow: "0 0 8px #4ade80",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      color: "rgba(255,255,255,.7)",
                      fontSize: ".74rem",
                      fontWeight: 600,
                    }}
                  >
                    {subjects.length} subject{subjects.length !== 1 ? "s" : ""}{" "}
                    available
                  </Typography>
                </ExamBadge>
                <Typography
                  sx={{
                    fontFamily: "'DM Serif Display',serif",
                    fontSize: { xs: "1.8rem", md: "2.3rem" },
                    color: "#fff",
                    lineHeight: 1.14,
                    mb: 0.5,
                  }}
                >
                  {examName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "rgba(255,255,255,.5)",
                    fontSize: ".88rem",
                  }}
                >
                  Select a subject below to begin your examination
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Hero>

      {/* CONTENT */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.5rem",
                  color: "#0f172a",
                  mb: 0.4,
                }}
              >
                Available Subjects
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#94a3b8",
                  fontSize: ".84rem",
                }}
              >
                Click any subject to review instructions and begin your exam
              </Typography>
            </Box>
            {!loading && !error && subjects.length > 0 && (
              <Box
                sx={{
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  borderRadius: "10px",
                  px: 2,
                  py: 0.8,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".75rem",
                    fontWeight: 700,
                    color: "#92400e",
                  }}
                >
                  {subjects.length} Subject{subjects.length !== 1 ? "s" : ""} ·
                  JAMB
                </Typography>
              </Box>
            )}
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 14,
                gap: 2,
              }}
            >
              <CircularProgress
                size={46}
                thickness={4}
                sx={{ color: "#f59e0b" }}
              />
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#94a3b8",
                  fontSize: ".86rem",
                }}
              >
                Loading subjects…
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                textAlign: "center",
                py: 12,
                background: "#fff",
                borderRadius: "22px",
                border: "1.5px solid #fee2e2",
              }}
            >
              <Typography sx={{ fontSize: "2.5rem", mb: 2 }}>⚠️</Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  color: "#dc2626",
                  fontSize: ".95rem",
                }}
              >
                {error}
              </Typography>
            </Box>
          ) : subjects.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2,1fr)",
                  md: "repeat(3,1fr)",
                },
                gap: 3,
              }}
            >
              {subjects.map((subject, i) => {
                const pal = PALETTES[i % PALETTES.length];
                const icon = ICONS[i % ICONS.length];
                const isSelected = selectedSubjectId === subject.id;
                return (
                  <Fade in timeout={300 + i * 80} key={subject.id}>
                    <Box
                      sx={{
                        animation: `${fadeUp} ${
                          0.4 + i * 0.07
                        }s cubic-bezier(.22,1,.36,1) both`,
                        animationDelay: `${i * 0.06}s`,
                      }}
                    >
                      <SubCard
                        pal={pal}
                        selected={isSelected}
                        onClick={() => openModal(subject.id)}
                      >
                        <Box sx={{ p: "24px 24px 20px" }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "16px",
                              background: `linear-gradient(135deg,${pal.from},${pal.to})`,
                              boxShadow: `0 10px 24px -6px ${pal.shadow}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "1.45rem",
                              mb: 2.2,
                            }}
                          >
                            {icon}
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              fontWeight: 700,
                              fontSize: ".98rem",
                              color: "#0f172a",
                              mb: 0.45,
                            }}
                          >
                            {subject.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              color: "#94a3b8",
                              fontSize: ".78rem",
                              mb: 2.8,
                            }}
                          >
                            Click to review instructions & begin
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                background: pal.light,
                                color: pal.text,
                                fontFamily: "'Sora',sans-serif",
                                fontSize: ".68rem",
                                fontWeight: 700,
                                px: 1.3,
                                py: 0.45,
                                borderRadius: 100,
                                letterSpacing: ".06em",
                              }}
                            >
                              JAMB
                            </Box>
                            <BeginBtn
                              pal={pal}
                              variant="contained"
                              endIcon={
                                <PlayArrowIcon
                                  sx={{ fontSize: ".9rem !important" }}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal(subject.id);
                              }}
                            >
                              Begin
                            </BeginBtn>
                          </Box>
                        </Box>
                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              background: pal.from,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: `0 4px 12px ${pal.shadow}`,
                              animation: `${ripple} 2s ease-in-out infinite`,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{ color: "#fff", fontSize: 14 }}
                            />
                          </Box>
                        )}
                      </SubCard>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 13,
                background: "#fff",
                borderRadius: "22px",
                border: "1.5px dashed #fde68a",
              }}
            >
              <Typography sx={{ fontSize: "3rem", mb: 2 }}>📭</Typography>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.4rem",
                  color: "#0f172a",
                  mb: 1,
                }}
              >
                No subjects yet
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#94a3b8",
                  fontSize: ".86rem",
                }}
              >
                No active subjects available for JAMB at this time.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* INSTRUCTIONS MODAL */}
      {selectedSubjectId && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(12,10,0,.76)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 300,
            p: 2,
          }}
          onClick={closeModal}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              maxWidth: 480,
              background: "#fff",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.3)",
              animation: `${fadeUp} .35s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            {/* Modal header — JAMB amber theme */}
            <Box
              sx={{
                background: "linear-gradient(135deg,#0c0a00 0%,#1c1000 100%)",
                p: "28px 32px 24px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <HeroGrid />
              <HeroOrb
                l="-60px"
                t="-60px"
                size="180px"
                color="radial-gradient(circle,rgba(180,83,9,.45),transparent)"
              />
              <HeroOrb
                l="60%"
                t="30%"
                size="120px"
                color="radial-gradient(circle,rgba(245,158,11,.2),transparent)"
              />
              {/* JAMB label */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "rgba(245,158,11,.12)",
                  border: "1px solid rgba(245,158,11,.25)",
                  borderRadius: 100,
                  px: 1.5,
                  py: 0.4,
                  mb: 1.2,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".65rem",
                    fontWeight: 700,
                    color: "#fbbf24",
                    letterSpacing: ".1em",
                  }}
                >
                  JAMB · BEFORE YOU BEGIN
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.6rem",
                  color: "#fff",
                  lineHeight: 1.2,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {subjects.find((s) => s.id === selectedSubjectId)?.name ||
                  "Exam"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "rgba(255,255,255,.42)",
                  fontSize: ".82rem",
                  mt: 0.4,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Exam Instructions
              </Typography>
            </Box>

            {/* Instructions */}
            <Box sx={{ p: "24px 32px 6px" }}>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: ".68rem",
                  color: "#475569",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Please read carefully
              </Typography>
              {instructions.map((ins, i) => (
                <InsRow key={i}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "8px",
                      background: "#fef3c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.15,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        fontSize: ".7rem",
                        color: "#b45309",
                      }}
                    >
                      {i + 1}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".86rem",
                      color: "#334155",
                      lineHeight: 1.62,
                    }}
                  >
                    {ins}
                  </Typography>
                </InsRow>
              ))}
              <Box
                sx={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "11px",
                  p: "10px 14px",
                  mt: 1.5,
                  mb: 0.5,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={{ fontSize: ".9rem" }}>⚠️</Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".8rem",
                    color: "#92400e",
                    lineHeight: 1.6,
                  }}
                >
                  Once started, the timer <b>cannot be paused</b>. Ensure
                  you're fully ready before proceeding.
                </Typography>
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ p: "16px 32px 28px", display: "flex", gap: 2 }}>
              <Button
                onClick={closeModal}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  py: 1.35,
                  fontSize: ".86rem",
                  color: "#64748b",
                  borderColor: "#e2e8f0",
                  borderWidth: 1.5,
                  "&:hover": {
                    background: "#fffffa",
                    borderColor: "#cbd5e1",
                  },
                }}
              >
                Go Back
              </Button>
              <Button
                onClick={startExam}
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.35,
                  fontSize: ".86rem",
                  background: "linear-gradient(105deg,#b45309,#f59e0b)",
                  boxShadow: "0 10px 24px -6px rgba(180,83,9,.4)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 14px 30px -6px rgba(180,83,9,.5)",
                  },
                  transition: "all .28s ease",
                }}
              >
                Start Exam
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        component="footer"
        sx={{
          background: "#07101f",
          borderTop: "1px solid rgba(255,255,255,.05)",
          py: 2.5,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            color: "rgba(255,255,255,.3)",
            fontSize: ".76rem",
          }}
        >
          © {new Date().getFullYear()} LiteUpper. All rights reserved. · support@liteupper.com · 08100939586
        </Typography>
      </Box>
    </Root>
  );
}